// Current application version
export const APP_VERSION = '0.4.1';

// Changelog entries by version
export const CHANGELOG_DATA = {
  '0.4.1': {
    date: 'April 2, 2025',
    changes: [
      'Fixed undo functionality with "z" key to properly remove spaces',
      'Resolved keyboard shortcut conflict between undo and reset view',
      'Added URL-based navigation with browser history support',
      'Implemented proper back button functionality between app sections',
      'Improved event handling to prevent conflicts between components'
    ]
  },
  '0.4.0': {
    date: 'April 1, 2025',
    changes: [
      'Added project sorting for both My Projects and All Projects views',
      'Added "Install q\'s" button (replacing "Details") for deployment service questions',
      'Fixed scale tool to properly handle rescaling of existing measurements',
      'Added detailed debugging for square footage calculations to prevent runaway numbers',
      'Improved buttons and styling throughout the project summary page',
      'Added Grid and List view options to the projects page for better organization',
      'Fixed negative dimensions display in box area calculations',
      'Added square footage calculation details with dimensions to table rows',
      'Improved area display with clear dimensional math formulas',
      'Removed "Set scale first" text for cleaner interface'
    ]
  },
  '0.3.9': {
    date: 'March 19, 2025',
    changes: [
      'Moved drawing tools to the top navigation bar for easier access',
      'Added space type icons (desk, room, phone booth, flex) with keyboard shortcuts',
      'Improved multi-select functionality with clearer visual feedback',
      'Fixed save button to the top of screen for better visibility',
      'Reduced notification messages for a cleaner interface',
      'Added toggle button to hide/show tool panel as needed',
      'Streamlined workflow with space types that work with both Box and Free Form drawing tools'
    ]
  },
  '0.3.8': {
    date: 'April 24, 2024',
    changes: [
      'Added user authentication with Supabase',
      'Implemented account creation and login functionality',
      'Added support for logging in with density.io email addresses',
      'Implemented project management with save and load capabilities',
      'Added auto-save functionality for projects',
      'Improved UI with clearer save status indicators',
      'Fixed dependency issues for more stable builds'
    ]
  },
  '0.3.7': {
    date: 'March 14, 2024 - 12:48pm PT',
    changes: [
      'Added support for PDF file uploads with automatic conversion',
      'Expanded file format support to include PNG, JPG, and PDF files',
      'Implemented PDF processing with optimized rendering',
      'Temporarily disabled ceiling height feature to simplify user interface',
      'Streamlined control panel by removing ceiling height button',
      'Enhanced user experience by reducing interface complexity'
    ]
  },
  '0.3.6': {
    date: 'March 13, 2024 - 10:28pm PT',
    changes: [
      'Added drag & drop file upload with PNG/JPG validation and visual feedback',
      'Implemented keyboard zoom controls (+/-) with centered zoom and level indicator',
      'Enhanced multi-select and delete functionality with keyboard shortcuts',
      'Added direct click-to-edit for Waffle space volumes with count indicators',
      'Improved selection handling with cross-platform keyboard support'
    ]
  },
  '0.3.5': {
    date: 'March 16, 2024',
    changes: [
      'Fixed box positioning issues with zoom to maintain proper alignment',
      'Simplified coordinate system by moving SVG overlay inside the transformed container',
      'Improved rendering of all elements (boxes, shapes, entry points) at different zoom levels',
      'Fixed element sizing and positioning to remain consistent during zoom operations',
      'Enhanced interaction with space elements at all zoom levels'
    ]
  },
  '0.3.4': {
    date: 'March 15, 2024',
    changes: [
      'Fixed box positioning issues with zoom to maintain proper alignment',
      'Improved coordinate transformations for consistent positioning at all zoom levels',
      'Added count indicators for Waffle spaces with count > 1',
      'Enhanced text visibility with dynamic font sizing based on zoom level',
      'Fixed entry point scaling to maintain proportions across different zoom levels',
      'Improved overall visual consistency when navigating the floorplan'
    ]
  },
  '0.3.3': {
    date: 'March 14, 2024',
    changes: [
      'Added count editing for Waffle spaces with click-to-edit functionality',
      'Waffle counts now default to 1 and can be directly edited',
      'Repositioned zoom indicator to prevent overlap with tools',
      'Improved user experience with simple, direct editing'
    ]
  },
  '0.3.2': {
    date: 'March 14, 2024',
    changes: [
      'Added drag & drop file upload functionality',
      'Improved upload UI with visual feedback during drag operations',
      'Added file type validation with support for PNG and JPG formats',
      'Added helpful upload tips and supported format indicators',
      'Implemented componentized approach for better code organization'
    ]
  },
  '0.3.1': {
    date: 'March 14, 2024',
    changes: [
      'Added simple keyboard zoom functionality',
      'Press "+" key to zoom in by 20%',
      'Press "-" key to zoom out by 20%',
      'Added zoom level indicator',
      'Zoom is centered on the floorplan'
    ]
  },
  '0.3.0': {
    date: 'March 13, 2024',
    changes: [
      'Added undo functionality with "z" key for reversing actions',
      'Implemented minimum space size (2 sq ft) with user-friendly error messages',
      'Added sortable table with column sorting for spaces list',
      'Improved table organization with visual sort indicators',
      'Rounded up area values for cleaner display (no decimal points)',
      'Fixed sorting for sensor type column',
      'Repositioned "Set Scale" dialog beneath the map for better ergonomics',
      'Added Enter key support for scale input field',
      'Removed zoom functionality to simplify the interface',
      'Added text labels to all tool buttons for better clarity',
      'Fixed image loading behavior',
      'Auto-activation of scale tool upon image upload'
    ]
  },
  '0.2.0': {
    date: 'March 12, 2024',
    changes: [
      'Fixed issue where the freeform drawing tool wasn\'t correctly respecting the scale',
      'Implemented ESC key functionality to cancel any active tool operation',
      'Added ability to toggle individual spaces on/off',
      'Implemented a new space type "Entry" with purple dot visualization',
      'All spaces now use a single sequential numbering system',
      'Added door icon (🚪) for the entry point button',
      'Added entry points to the spaces list table'
    ]
  },
  '0.1.0': {
    date: 'March 12, 2024',
    changes: [
      'Initial release of Romulus floorplan detector',
      'Basic floorplan upload and viewing functionality',
      'Space creation and management tools',
      'Scale tool for accurate measurements'
    ]
  }
};

// Get changes for the current version
export const getCurrentVersionChanges = () => {
  return CHANGELOG_DATA[APP_VERSION]?.changes || [];
};

// Get date for the current version
export const getCurrentVersionDate = () => {
  return CHANGELOG_DATA[APP_VERSION]?.date || '';
}; 